<template>
    <div class="container">
        <div class="fixed-header">
            <van-nav-bar title="比赛报名" left-text="返回" left-arrow @click-left="onClickLeft" class="custom-nav-bar" />
        </div>

        <div class="content-section">
            <div v-if="matchSubjects.length === 0" class="empty-state">
                <van-empty description="暂无可报名的比赛" />
            </div>

            <div v-else class="match-list">
                <!-- 已报名的比赛 -->
                <div v-if="signedMatches.length > 0" class="merchant-group my-matches">
                    <div class="merchant-header">
                        <van-icon name="certificate" class="merchant-icon" />
                        <span class="merchant-name">我的报名</span>
                    </div>
                    <div class="match-cards">
                        <div v-for="match in signedMatches" :key="match.id" class="match-card">
                            <div class="match-header">
                                <span class="match-name">{{ match.matchSubjectName }}</span>
                                <span :class="['status-tag', getStatusClass(match.applyStatus)]">
                                    {{ getStatusText(match.applyStatus) }}
                                </span>
                            </div>

                            <div class="match-info">
                                <div class="info-item merchant-name-item">
                                    <van-icon name="shop" class="info-icon" />
                                    <span>{{ match.merchantName }}</span>
                                </div>
                                <div class="info-item">
                                    <van-icon name="clock-o" class="info-icon" />
                                    <span>{{ formatDateTime(match.startDateTime) }}</span>
                                </div>
                                <div class="info-item">
                                    <van-icon name="friends-o" class="info-icon" />
                                    <span>{{ match.bowlerCountPerTeam }}人/组</span>
                                </div>
                                <div class="info-item">
                                    <van-icon name="replay" class="info-icon" />
                                    <span>{{ match.turnCount }}轮</span>
                                </div>
                                <div v-if="match.matchItemType === 11" class="info-item">
                                    <van-icon name="star-o" class="info-icon" />
                                    <span>贝壳赛 {{ match.beikeTurnCount }}轮</span>
                                </div>
                                <div class="info-item">
                                    <van-icon name="label-o" class="info-icon" />
                                    <span>{{ getMatchTypeName(match.matchItemType) }}</span>
                                </div>
                                <div class="info-item">
                                    <van-icon name="user-circle-o" class="info-icon" />
                                    <span v-if="match.bowlerMaxCount">已报名 {{ match.appliedBowlerCount || 0 }} / {{
                                        match.bowlerMaxCount }}人</span>
                                    <span v-else>已报名 {{ match.appliedBowlerCount || 0 }}人</span>
                                </div>
                                <div v-if="match.remark" class="info-item remark-item">
                                    <van-icon name="comment-o" class="info-icon" />
                                    <span>{{ match.remark }}</span>
                                </div>
                                <div v-if="match.applyStatus === 2" class="info-item reject-reason">
                                    <van-icon name="warning-o" class="info-icon warning" />
                                    <span>拒绝原因：{{ match.applyResultObj?.rejectedReason || '无' }}</span>
                                </div>
                                <div v-if="(match.canExitApply || match.canApply) && (!match.sessionSubjects || match.sessionSubjects.length === 0) && (!match.bowlerMaxCount || (match.appliedBowlerCount || 0) < match.bowlerMaxCount)"
                                    class="action-area">
                                    <van-button :type="match.canExitApply ? 'danger' : 'primary'" size="small"
                                        @click="match.canExitApply ? handleQuit(match) : handleSignup(match)">
                                        {{ match.canExitApply ? '取消报名' : '立即报名' }}
                                    </van-button>
                                </div>

                                <!-- 子场次信息 -->
                                <template v-if="match.sessionSubjects && match.sessionSubjects.length > 0">
                                    <div class="session-subjects">
                                        <div class="session-title">子场次信息</div>
                                        <div v-for="(session, index) in match.sessionSubjects" :key="session.id"
                                            class="session-card">
                                            <div class="session-header">
                                                <span class="session-name">第{{ session.sessionNumber }}场</span>
                                                <span v-if="match.applyStatus !== 1 && session.applyStatus !== null"
                                                    :class="['status-tag', getStatusClass(session.applyStatus)]">
                                                    {{ getStatusText(session.applyStatus) }}
                                                </span>
                                            </div>
                                            <div class="session-info">
                                                <div class="info-item">
                                                    <van-icon name="clock-o" class="info-icon" />
                                                    <span>{{ formatDateTime(session.startDateTime) }}</span>
                                                </div>
                                                <div class="info-item">
                                                    <van-icon name="user-circle-o" class="info-icon" />
                                                    <span v-if="session.bowlerMaxCount">已报名 {{
                                                        (session.appliedBowlerCount || 0) }} / {{
                                                            session.bowlerMaxCount }}人</span>
                                                    <span v-else>已报名 {{ (session.appliedBowlerCount || 0) }}人</span>
                                                </div>
                                                <div v-if="session.remark" class="info-item remark-item">
                                                    <van-icon name="comment-o" class="info-icon" />
                                                    <span>{{ session.remark }}</span>
                                                </div>
                                                <div v-if="session.canApply && (!match.bowlerMaxCount || (match.appliedBowlerCount || 0) < match.bowlerMaxCount)"
                                                    class="session-action">
                                                    <van-button type="primary" size="small"
                                                        @click="handleSignup(session)">
                                                        立即报名
                                                    </van-button>
                                                </div>
                                                <div v-if="session.canExitApply" class="session-action">
                                                    <van-button type="danger" size="small" @click="handleQuit(session)">
                                                        取消报名
                                                    </van-button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 全部比赛 -->
                <div class="section-title">全部比赛</div>
                <div v-for="group in groupedMatches" :key="group.merchantName" class="merchant-group">
                    <div class="merchant-header" @click="toggleMerchant(group.merchantName)">
                        <div class="merchant-title">
                            <van-icon name="shop" class="merchant-icon" />
                            <span class="merchant-name">{{ group.merchantName }}</span>
                        </div>
                        <div class="merchant-summary" v-if="!expandedMerchants[group.merchantName]">
                            <span class="match-count">{{ group.matches.length }}个比赛</span>
                            <van-icon name="arrow-down" class="expand-icon" />
                        </div>
                        <div class="merchant-summary" v-else>
                            <van-icon name="arrow-up" class="expand-icon" />
                        </div>
                    </div>
                    <div v-show="expandedMerchants[group.merchantName]" class="match-cards">
                        <div v-for="match in group.matches" :key="match.id" class="match-card">
                            <div class="match-header">
                                <span class="match-name">{{ match.matchSubjectName }}</span>
                                <span :class="['status-tag', getStatusClass(match.applyStatus)]">
                                    {{ getStatusText(match.applyStatus) }}
                                </span>
                            </div>

                            <div class="match-info">
                                <div class="info-item merchant-name-item">
                                    <van-icon name="shop" class="info-icon" />
                                    <span>{{ match.merchantName }}</span>
                                </div>
                                <div v-if="!match.sessionSubjects || match.sessionSubjects.length === 0"
                                    class="info-item">
                                    <van-icon name="clock-o" class="info-icon" />
                                    <span>{{ formatDateTime(match.startDateTime) }}</span>
                                </div>
                                <div class="info-item">
                                    <van-icon name="friends-o" class="info-icon" />
                                    <span>{{ match.bowlerCountPerTeam }}人/组</span>
                                </div>
                                <div class="info-item">
                                    <van-icon name="replay" class="info-icon" />
                                    <span>{{ match.turnCount }}轮</span>
                                </div>
                                <div v-if="match.matchItemType === 11" class="info-item">
                                    <van-icon name="star-o" class="info-icon" />
                                    <span>贝壳赛 {{ match.beikeTurnCount }}轮</span>
                                </div>
                                <div class="info-item">
                                    <van-icon name="label-o" class="info-icon" />
                                    <span>{{ getMatchTypeName(match.matchItemType) }}</span>
                                </div>
                                <div class="info-item">
                                    <van-icon name="user-circle-o" class="info-icon" />
                                    <span v-if="match.bowlerMaxCount">已报名 {{ match.appliedBowlerCount || 0 }} / {{
                                        match.bowlerMaxCount
                                        }}人</span>
                                    <span v-else>已报名 {{ match.appliedBowlerCount || 0 }}人</span>
                                </div>
                                <div v-if="match.remark" class="info-item remark-item">
                                    <van-icon name="comment-o" class="info-icon" />
                                    <span>{{ match.remark }}</span>
                                </div>
                                <div v-if="match.applyStatus === 2" class="info-item reject-reason">
                                    <van-icon name="warning-o" class="info-icon warning" />
                                    <span>拒绝原因：{{ match.applyResultObj?.rejectedReason || '无' }}</span>
                                </div>

                                <!-- 子场次信息 -->
                                <template v-if="match.sessionSubjects && match.sessionSubjects.length > 0">
                                    <div class="session-subjects">
                                        <div class="session-title">子场次信息</div>
                                        <div v-for="(session, index) in match.sessionSubjects" :key="session.id"
                                            class="session-card">
                                            <div class="session-header">
                                                <span class="session-name">第{{ session.sessionNumber }}场</span>
                                                <span v-if="match.applyStatus !== 1 && session.applyStatus !== null"
                                                    :class="['status-tag', getStatusClass(session.applyStatus)]">
                                                    {{ getStatusText(session.applyStatus) }}
                                                </span>
                                            </div>
                                            <div class="session-info">
                                                <div class="info-item">
                                                    <van-icon name="clock-o" class="info-icon" />
                                                    <span>{{ formatDateTime(session.startDateTime) }}</span>
                                                </div>
                                                <div class="info-item">
                                                    <van-icon name="user-circle-o" class="info-icon" />
                                                    <span v-if="session.bowlerMaxCount">已报名 {{
                                                        (session.appliedBowlerCount || 0) }} / {{
                                                            session.bowlerMaxCount }}人</span>
                                                    <span v-else>已报名 {{ (session.appliedBowlerCount || 0) }}人</span>
                                                </div>
                                                <div v-if="session.remark" class="info-item remark-item">
                                                    <van-icon name="comment-o" class="info-icon" />
                                                    <span>{{ session.remark }}</span>
                                                </div>
                                                <div v-if="session.canApply && (!match.bowlerMaxCount || (match.appliedBowlerCount || 0) < match.bowlerMaxCount)"
                                                    class="session-action">
                                                    <van-button type="primary" size="small"
                                                        @click="handleSignup(session)">
                                                        立即报名
                                                    </van-button>
                                                </div>
                                                <div v-if="session.canExitApply" class="session-action">
                                                    <van-button type="danger" size="small" @click="handleQuit(session)">
                                                        取消报名
                                                    </van-button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <div v-if="(match.canExitApply || match.canApply) && (!match.sessionSubjects || match.sessionSubjects.length === 0) && (!match.bowlerMaxCount || (match.appliedBowlerCount || 0) < match.bowlerMaxCount)"
                                    class="action-area">
                                    <van-button :type="match.canExitApply ? 'danger' : 'primary'" size="small"
                                        @click="match.canExitApply ? handleQuit(match) : handleSignup(match)">
                                        {{ match.canExitApply ? '取消报名' : '立即报名' }}
                                    </van-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import util from "@/global/util";
import { showToast, showDialog } from 'vant';
import matchApi from '@/api/match';

const router = useRouter();
const matchSubjects = ref([]);
const expandedMerchants = ref({}); // 用于存储商家展开状态

// 切换商家卡片展开状态
const toggleMerchant = (merchantName) => {
    expandedMerchants.value[merchantName] = !expandedMerchants.value[merchantName];
};

// 已报名的比赛
const signedMatches = computed(() => {
    return matchSubjects.value.filter(match => match.applyStatus !== null);
});

// 模拟数据
const mockMatchSubjects = [
    {
        id: 1,
        merchantName: '星光保龄球馆',
        startDateTime: '2024-01-20 14:00:00',
        matchSubjectName: '新年欢乐赛',
        matchItemType: 11,
        bowlerCountPerTeam: 4,
        turnCount: 3,
        beikeTurnCount: 2,
        applyStatus: null,
        appliedBowlerCount: 12,
        bowlerMaxCount: 16,
        applyResultObj: null,
        canApply: false,
        canExitApply: false,
        sessionSubjects: [
            {
                id: 101,
                merchantName: '星光保龄球馆',
                startDateTime: '2024-01-20 14:00:00',
                matchSubjectName: '上午场',
                matchItemType: 11,
                bowlerCountPerTeam: 4,
                turnCount: 3,
                beikeTurnCount: 2,
                applyStatus: null,
                appliedBowlerCount: 6,
                applyResultObj: null,
                canApply: true,
                canExitApply: false,
                sessionNumber: 1,
            },
            {
                id: 102,
                merchantName: '星光保龄球馆',
                startDateTime: '2024-01-20 19:00:00',
                matchSubjectName: '下午场',
                matchItemType: 11,
                bowlerCountPerTeam: 4,
                turnCount: 3,
                beikeTurnCount: 2,
                applyStatus: null,
                appliedBowlerCount: 6,
                applyResultObj: null,
                canApply: true,
                canExitApply: false,
                sessionNumber: 2,
            }
        ]
    },
    {
        id: 2,
        merchantName: '星光保龄球馆',
        startDateTime: '2024-01-25 10:00:00',
        matchSubjectName: '周末双人赛',
        matchItemType: 0,
        bowlerCountPerTeam: 2,
        turnCount: 4,
        applyStatus: null,
        appliedBowlerCount: 0,
        applyResultObj: null,
        canApply: false,
        canExitApply: false,
        sessionSubjects: [
            {
                id: 201,
                merchantName: '星光保龄球馆',
                startDateTime: '2024-01-25 10:00:00',
                matchSubjectName: '初级组',
                matchItemType: 0,
                bowlerCountPerTeam: 2,
                turnCount: 4,
                applyStatus: null,
                appliedBowlerCount: 0,
                applyResultObj: null,
                canApply: true,
                canExitApply: false,
                sessionNumber: 1,
            },
            {
                id: 202,
                merchantName: '星光保龄球馆',
                startDateTime: '2024-01-25 14:00:00',
                matchSubjectName: '中级组',
                matchItemType: 0,
                bowlerCountPerTeam: 2,
                turnCount: 4,
                applyStatus: null,
                appliedBowlerCount: 0,
                applyResultObj: null,
                canApply: true,
                canExitApply: false,
                sessionNumber: 2,
            },
            {
                id: 203,
                merchantName: '星光保龄球馆',
                startDateTime: '2024-01-25 19:00:00',
                matchSubjectName: '高级组',
                matchItemType: 0,
                bowlerCountPerTeam: 2,
                turnCount: 4,
                applyStatus: null,
                appliedBowlerCount: 0,
                applyResultObj: null,
                canApply: true,
                canExitApply: false,
                sessionNumber: 4,
            }
        ]
    },
    {
        id: 2,
        merchantName: '星光保龄球馆',
        startDateTime: '2024-01-25 10:00:00',
        matchSubjectName: '周末双人赛',
        matchItemType: 0,
        bowlerCountPerTeam: 2,
        turnCount: 4,
        applyStatus: null,
        appliedBowlerCount: 0,
        applyResultObj: null,
        canApply: true,
        canExitApply: false,
    },
    {
        id: 3,
        merchantName: '星光保龄球馆',
        startDateTime: '2024-02-01 15:30:00',
        matchSubjectName: '情人节特别赛',
        matchItemType: 11,
        bowlerCountPerTeam: 3,
        turnCount: 5,
        beikeTurnCount: 3,
        applyStatus: 1,
        appliedBowlerCount: 24,
        bowlerMaxCount: 24,
        remark: '本场比赛为情人节特别活动，情侣组队可获额外奖励',
        applyResultObj: null,
        canApply: false,
        canExitApply: false,
    },
    {
        id: 4,
        merchantName: '银河保龄球馆',
        startDateTime: '2024-01-22 09:00:00',
        matchSubjectName: '新手友谊赛',
        matchItemType: 0,
        bowlerCountPerTeam: 2,
        turnCount: 3,
        applyStatus: null,
        appliedBowlerCount: 0,
        bowlerMaxCount: 20,
        remark: '适合初次参赛的选手，有专业教练现场指导',
        applyResultObj: null,
        canApply: true,
        canExitApply: false,
    },
    {
        id: 5,
        merchantName: '银河保龄球馆',
        startDateTime: '2024-01-28 14:30:00',
        matchSubjectName: '精英挑战赛',
        matchItemType: 11,
        bowlerCountPerTeam: 4,
        turnCount: 6,
        beikeTurnCount: 4,
        applyStatus: 2,
        appliedBowlerCount: 16,
        applyResultObj: {
            rejectedReason: "报名人数已满"
        },
        canApply: false,
        canExitApply: false,
    },
    {
        id: 6,
        merchantName: '阳光保龄球馆',
        startDateTime: '2024-01-21 13:00:00',
        matchSubjectName: '周日欢乐赛',
        matchItemType: 0,
        bowlerCountPerTeam: 3,
        turnCount: 4,
        applyStatus: null,
        appliedBowlerCount: 8,
        applyResultObj: null,
        canApply: true,
        canExitApply: false,
    },
    {
        id: 7,
        merchantName: '阳光保龄球馆',
        startDateTime: '2024-02-04 10:00:00',
        matchSubjectName: '新春团体赛',
        matchItemType: 11,
        bowlerCountPerTeam: 6,
        turnCount: 5,
        beikeTurnCount: 3,
        applyStatus: null,
        appliedBowlerCount: 18,
        applyResultObj: null,
        canApply: false,
        canExitApply: false,
        sessionSubjects: [
            {
                id: 701,
                merchantName: '阳光保龄球馆',
                startDateTime: '2024-02-04 10:00:00',
                matchSubjectName: '团体赛A组',
                matchItemType: 11,
                bowlerCountPerTeam: 6,
                turnCount: 5,
                beikeTurnCount: 3,
                applyStatus: null,
                appliedBowlerCount: 6,
                applyResultObj: null,
                canApply: true,
                canExitApply: false,
                sessionNumber: 1,
            },
            {
                id: 702,
                merchantName: '阳光保龄球馆',
                startDateTime: '2024-02-04 14:00:00',
                matchSubjectName: '团体赛B组',
                matchItemType: 11,
                bowlerCountPerTeam: 6,
                turnCount: 5,
                beikeTurnCount: 3,
                applyStatus: null,
                appliedBowlerCount: 12,
                applyResultObj: null,
                canApply: true,
                canExitApply: false,
                sessionNumber: 2,
            }
        ]
    },
    {
        id: 8,
        merchantName: '巨谷保龄球馆',
        startDateTime: '2024-01-23 19:00:00',
        matchSubjectName: '夜场精英赛',
        matchItemType: 0,
        bowlerCountPerTeam: 2,
        turnCount: 4,
        applyStatus: 0,
        canApply: false,
        canExitApply: true,
    },
    {
        id: 9,
        merchantName: '巨谷保龄球馆',
        startDateTime: '2024-01-30 15:00:00',
        matchSubjectName: '寒假青少年赛',
        matchItemType: 0,
        bowlerCountPerTeam: 2,
        turnCount: 3,
        applyStatus: null,
        appliedBowlerCount: 4,
        bowlerMaxCount: 30,
        applyResultObj: null,
        canApply: true,
        canExitApply: false,
    },
    {
        id: 10,
        merchantName: '巨谷保龄球馆',
        startDateTime: '2024-02-10 14:00:00',
        matchSubjectName: '元宵节团体赛',
        matchItemType: 11,
        bowlerCountPerTeam: 4,
        turnCount: 5,
        beikeTurnCount: 3,
        applyStatus: null,
        appliedBowlerCount: 0,
        applyResultObj: null,
        canApply: false,
        canExitApply: false,
        sessionSubjects: [
            {
                id: 1001,
                merchantName: '巨谷保龄球馆',
                startDateTime: '2024-02-10 10:00:00',
                matchSubjectName: '欢乐组',
                matchItemType: 11,
                bowlerCountPerTeam: 4,
                turnCount: 5,
                beikeTurnCount: 3,
                applyStatus: null,
                appliedBowlerCount: 0,
                applyResultObj: null,
                canApply: true,
                canExitApply: false,
                sessionNumber: 2,
            },
            {
                id: 1002,
                merchantName: '巨谷保龄球馆',
                startDateTime: '2024-02-10 14:00:00',
                matchSubjectName: '精英组',
                matchItemType: 11,
                bowlerCountPerTeam: 4,
                turnCount: 5,
                beikeTurnCount: 3,
                applyStatus: null,
                appliedBowlerCount: 0,
                applyResultObj: null,
                canApply: true,
                canExitApply: false,
                sessionNumber: 3,
            },
            {
                id: 1003,
                merchantName: '巨谷保龄球馆',
                startDateTime: '2024-02-10 19:00:00',
                matchSubjectName: '大师组',
                matchItemType: 11,
                bowlerCountPerTeam: 4,
                turnCount: 5,
                beikeTurnCount: 3,
                applyStatus: null,
                appliedBowlerCount: 0,
                applyResultObj: null,
                canApply: true,
                canExitApply: false,
                sessionNumber: 4,
            }
        ]
    }
];

// 按球馆分组的比赛数据
const groupedMatches = computed(() => {
    const groups = matchSubjects.value.reduce((acc, match) => {
        if (!acc[match.merchantName]) {
            acc[match.merchantName] = {
                merchantName: match.merchantName,
                matches: []
            };
        }
        acc[match.merchantName].matches.push(match);
        return acc;
    }, {});

    // 转换为数组并按球馆名称排序
    return Object.values(groups).sort((a, b) =>
        a.merchantName.localeCompare(b.merchantName, 'zh-CN')
    );
});

// onMounted(async () => {
//     try {
//         // 获取比赛数据
//         const data = await matchApi.getAllMatchSubjectAppliesForUser();
//         matchSubjects.value = data;
//     } catch (error) {
//         console.error('获取比赛数据失败:', error);
//         // 使用模拟数据
//         const data = mockMatchSubjects.map(match => {
//             // 处理主赛事状态
//             match.canApply = match.applyStatus === null;
//             match.canExitApply = match.applyStatus === 0;

//             // 处理子场次
//             if (match.sessionSubjects) {
//                 match.sessionSubjects = match.sessionSubjects.map(session => ({
//                     ...session,
//                     parentId: match.id,
//                     canApply: session.applyStatus === null && match.applyStatus !== 1,
//                     canExitApply: session.applyStatus === 0
//                 }));
//             }
//             return match;
//         });
//         matchSubjects.value = data;
//     }
// });

onMounted(async () => {
    // 模拟API调用
    // const data = mockMatchSubjects.map(match => {
    //     if (match.sessionSubjects) {
    //         match.sessionSubjects = match.sessionSubjects.map(session => ({
    //             ...session,
    //             parentId: match.id  // 为子场次添加父级ID
    //         }));
    //     }
    //     return match;
    // });
    // matchSubjects.value = data;
    await loadData();
});

const loadData = async () => {
    matchSubjects.value = await matchApi.getAllMatchSubjectAppliesForUser();
};

const formatDateTime = (datetime) => {
    if (!datetime) return '';
    const date = new Date(datetime);
    return util.formatDate(date, 'yyyy-MM-dd HH:mm');
};

const getMatchTypeName = (type) => {
    const types = {
        0: '普通赛',
        11: '贝壳赛',
        20: '娱乐赛'
    };
    return types[type] || '未知类型';
};

const getStatusText = (status) => {
    const statusMap = {
        0: '审核中',
        1: '已报名',
        2: '已拒绝'
    };
    return statusMap[status] || '';
};

const getStatusClass = (status) => {
    const statusClassMap = {
        0: 'status-pending',
        1: 'status-approved',
        2: 'status-rejected'
    };
    return statusClassMap[status] || '';
};

const handleSignup = (matchSubject) => {
    // 如果是子场次，添加parentId参数
    const query = { id: matchSubject.id };
    if (matchSubject.parentId) {
        query.parentId = matchSubject.parentId;
    }
    router.push({ path: '/signupMatchSubject', query });
};

const handleQuit = async (matchSubject) => {
    try {
        await showDialog({
            title: '确认',
            message: '确定要取消报名吗？',
            confirmButtonText: '确认取消',
            confirmButtonColor: '#ee0a24',
        });
        matchApi.cancelMyApplyInMatchSubject(matchSubject.id)
        showToast('取消报名成功');
        loadData();
    } catch (error) {
        // 用户取消了操作
        console.log('用户取消了操作');
    }
};

const getStatusTagType = (status) => {
    const typeMap = {
        0: 'warning',   // 审核中
        1: 'success',   // 已报名
        2: 'danger'     // 已拒绝
    };
    return typeMap[status] || 'default';
};

const onClickLeft = () => {
    router.back();
};
</script>

<style scoped>
.my-matches {
    border: 2px solid #2ecc71;
}

.my-matches .merchant-header {
    background: linear-gradient(135deg, #2ecc71, #27ae60);
}

.merchant-name-item {
    grid-column: 1 / -1;
    color: #2c3e50;
    font-weight: 500;
}

.section-title {
    padding: 0.427rem 0;
    font-size: 0.427rem;
    font-weight: 600;
    color: #2c3e50;
}

.container {
    position: relative;
    height: 100vh;
    background-color: #f5f7fa;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    display: flex;
    flex-direction: column;
}


.fixed-header {
    position: sticky;
    top: 0;
    z-index: 1000;
    flex-shrink: 0;
}

.content-section {
    padding: 0.427rem;
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.match-list {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    padding-bottom: 1.067rem;
}

/* 商户分组卡片 */
.merchant-group {
    background: white;
    border-radius: 0.427rem;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    margin-bottom: 0.533rem;
}

.merchant-group:last-child {
    margin-bottom: 0;
}

.merchant-header {
    cursor: pointer;
    user-select: none;
}

.merchant-header:hover {
    background: linear-gradient(135deg, #3498db, #2980b9);
    opacity: 0.9;
}

.merchant-title {
    display: flex;
    align-items: center;
}

.merchant-summary {
    display: flex;
    align-items: center;
    gap: 0.267rem;
    font-size: 0.373rem;
}

.match-count {
    color: rgba(255, 255, 255, 0.9);
}

.expand-icon {
    font-size: 0.427rem;
    transition: transform 0.3s ease;
}

/* 我的报名特殊样式 */
.my-matches {
    border: 2px solid #2ecc71;
}

.my-matches .merchant-header {
    background: linear-gradient(135deg, #2ecc71, #27ae60);
}

.merchant-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.427rem 0.533rem;
    background: linear-gradient(135deg, #3498db, #2980b9);
    color: white;
}

.merchant-icon {
    margin-right: 0.267rem;
    font-size: 0.48rem;
}

.merchant-name {
    font-size: 0.453rem;
    font-weight: 600;
}

.match-cards {
    padding: 0.427rem;
}

/* 比赛卡片样式 */
.match-card {
    margin-bottom: 0.427rem;
    padding: 0.533rem;
    background: #f8f9fa;
    border-radius: 0.427rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
}

.match-card:last-child {
    margin-bottom: 0;
}

.match-card:hover {
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.match-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.427rem;
}

.match-name {
    font-size: 0.453rem;
    font-weight: 600;
    color: #2c3e50;
}

/* 状态标签样式 */
.status-tag {
    padding: 0.16rem 0.32rem;
    border-radius: 0.267rem;
    font-size: 0.347rem;
    font-weight: 500;
}

.status-pending {
    background-color: #f1c40f;
    color: #ffffff;
}

.status-approved {
    background-color: #2ecc71;
    color: #ffffff;
}

.status-rejected {
    background-color: #e74c3c;
    color: #ffffff;
}

/* 比赛信息布局 */
.match-info {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.32rem 0.533rem;
}

.info-item {
    display: flex;
    align-items: center;
    gap: 0.267rem;
    color: #7f8c8d;
    font-size: 0.373rem;
    line-height: 1.4;
}

.info-icon {
    font-size: 0.427rem;
    color: #3498db;
    flex-shrink: 0;
}

.info-icon.warning {
    color: #e74c3c;
}

/* 商家名称和拒绝原因占据整行 */
.merchant-name-item,
.reject-reason,
.remark-item {
    grid-column: 1 / -1;
}

.merchant-name-item {
    color: #2c3e50;
    font-weight: 500;
}

.reject-reason {
    color: #e74c3c;
    background: rgba(231, 76, 60, 0.1);
    padding: 0.267rem;
    border-radius: 0.267rem;
}

/* 操作区域 */
.action-area {
    grid-column: 1 / -1;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 0.427rem;
    padding-top: 0.427rem;
    border-top: 1px solid #eee;
}

/* 按钮样式 */
:deep(.van-button) {
    height: 0.853rem;
    padding: 0 0.533rem;
    border: none;
}

:deep(.van-button--primary) {
    background: linear-gradient(135deg, #3498db, #2980b9);
    box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
}

:deep(.van-button--danger) {
    background: linear-gradient(135deg, #e74c3c, #c0392b);
    box-shadow: 0 2px 8px rgba(231, 76, 60, 0.3);
}

:deep(.van-button:active) {
    transform: translateY(1px);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* 空状态 */
.empty-state {
    margin-top: 2.667rem;
    padding: 1.067rem;
    text-align: center;
}

:deep(.van-empty__description) {
    color: #7f8c8d;
    font-size: 0.373rem;
    margin-top: 0.533rem;
}

/* 分类标题 */
.section-title {
    padding: 0.427rem 0;
    font-size: 0.427rem;
    font-weight: 600;
    color: #2c3e50;
    margin-top: 0.533rem;
}

/* 子场次样式 */
.session-subjects {
    grid-column: 1 / -1;
    margin-top: 0.533rem;
    background: #f8f9fa;
    border-radius: 0.267rem;
    padding: 0.427rem;
}

.session-title {
    font-size: 0.373rem;
    color: #2c3e50;
    font-weight: 500;
    margin-bottom: 0.427rem;
    padding-bottom: 0.267rem;
    border-bottom: 1px solid #eee;
}

.session-card {
    background: white;
    border-radius: 0.267rem;
    padding: 0.427rem;
    margin-bottom: 0.427rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.session-card:last-child {
    margin-bottom: 0;
}

.session-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.267rem;
}

.session-name {
    font-size: 0.373rem;
    color: #2c3e50;
    font-weight: 500;
}

.session-info {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.267rem;
    align-items: center;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.267rem;
}

.session-action {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.267rem;
    grid-column: 1 / -1;
    display: flex;
    justify-content: flex-end;
    margin-top: 0.267rem;
    padding-top: 0.267rem;
    border-top: 1px solid #eee;
}
</style>